import { ActivityLevel } from "@/clients/models/ActivityLevel";
import { StepsButtons } from "@/components/button/steps-button";
import { ProfilePageContainer } from "@/components/container/profile-page-container";
import { LabelCheckbox } from "@/components/form/label-checkbox";
import { PageTitleComponent } from "@/components/page-title";
import { QueryKeys } from "@/constants/query-keys";
import { useGetActivityLevel } from "@/hooks/requests/acivity/use-get-activity-level";
import { useUpdateActivityLevel } from "@/hooks/requests/acivity/use-update-activity-level";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { ActivityLevelSchema } from "@/schemas/user-schema";
import { setProfileContext } from "@/utils/sentry/tools";
import { IonSkeletonText, useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";

const activityLevelData = [
  {
    name: "Sedentary",
    description: "(little or no exercise)",
    value: ActivityLevel.activityLevel.SEDENTARY_NO_EXERCISE,
  },
  {
    name: "Lightly active",
    description: "(light exercise 1 to 3 days/week)",
    value: ActivityLevel.activityLevel.LIGHTLY_ACTIVE_EXERCISE_1_3_DAYS_WEEK,
  },
  {
    name: "Moderately active",
    description: "Light to moderate exercise 4 to 5 days/ week)",
    value: ActivityLevel.activityLevel.MODERATELY_ACTIVE_EXERCISE_3_5_DAYS_WEEK,
  },
  {
    name: "Active",
    description:
      "(moderate exercise 6 to 7 days/week or intense exercise 3 to 4 times/week)",
    value: ActivityLevel.activityLevel.VERY_ACTIVE_EXERCISE_6_7_DAYS_A_WEEK,
  },
  {
    name: "Very active",
    description:
      "(intense exercise 6 to 7 times/week, sports training, or physical job)",
    value:
      ActivityLevel.activityLevel
        .EXTRA_ACTIVE_VERY_HARD_EXERCISE_AND_PHYSICAL_JOB,
  },
];

const ProfilingActivityPage = () => {
  const { info } = useStepRouteInfo();
  const navigation = useIonRouter();
  const { activityLevel, isLoading } = useGetActivityLevel();
  const { updateActivityLevel, isUpdating } = useUpdateActivityLevel();
  const { showError } = useToast();
  const queryClient = useQueryClient();
  if (isLoading) {
    return <IonSkeletonText animated />;
  }

  console.log("activityLevel", activityLevel);

  // Form
  const initialValues = {
    activityLevel: activityLevel?.activityLevel ?? "",
  };

  async function handleSubmit({ activityLevel }: { activityLevel: string }) {
    const formData = await ActivityLevelSchema.validate({
      activityLevel,
    });
    const dataBody: ActivityLevel = {
      activityLevel: formData.activityLevel as ActivityLevel.activityLevel,
    };

    // Get User Details

    updateActivityLevel(dataBody, {
      onSuccess: () => {
        navigation.push(info?.nextPath ?? "", "none");
        queryClient.invalidateQueries({ queryKey: [QueryKeys.ACTIVITY_LEVEL] });
        setProfileContext(QueryKeys.ACTIVITY_LEVEL, dataBody);
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  }

  return (
    <ProfilePageContainer>
      <PageTitleComponent
        title="How active are you day to day?"
        description="We'factor this in when it comes to your food."
        className="mt-2 mb-6 w-full"
      />

      <Formik
        initialValues={initialValues}
        validateOnMount={true}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={ActivityLevelSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className="flex flex-col align-bottom gap-4 h-full pb-20 overflow-y-auto no-scrollbar">
            {activityLevelData.map((item) => (
              <LabelCheckbox
                key={item.value}
                label={item.name}
                description={item.description}
                value={item.value}
                name="activityLevel"
                setFieldValue={setFieldValue}
                values={values}
              />
            ))}

            <StepsButtons
              previousPath={info?.previousPath}
              type="submit"
              disabled={isUpdating}
            />
          </Form>
        )}
      </Formik>
    </ProfilePageContainer>
  );
};

export default ProfilingActivityPage;
